/**
 * @fileoverview Shared email format and MX-record validation utilities.
 * @module ui-admin/utils
 *
 * Responsibilities:
 * - Provide a strict EMAIL_REGEX usable by Zod schemas and runtime checks.
 * - Provide validateEmailFormat() for synchronous format checks.
 * - Provide useEmailValidation() — a React hook that performs live format
 *   checking on every change and on-blur MX-record verification by calling
 *   POST /api/validate/email on the host app's API.
 *
 * Dependencies:
 * - react (peer)
 *
 * Architecture:
 * - Pure utility / host-neutral hook. No app-specific imports.
 * - The MX endpoint is reached via a relative URL (/api/validate/email) so that
 *   each host app's Vite dev proxy / production reverse proxy routes the call
 *   to its own API service (tenant-api / system-api / mobile-api).
 * - Offline-aware: when navigator.onLine is false, the MX check returns the
 *   'offline' status without making a network request.
 *
 * Notes:
 * - Promoted from web/tenant-web/src/utils/emailValidation.ts and
 *   web/system-web/src/utils/emailValidation.ts (which were near-identical
 *   copies). The tenant-web variant — which adds the offline status — is the
 *   canonical version preserved here.
 */

import { useState, useCallback, useRef } from 'react';

export const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

/**
 * Returns true if the email passes basic format validation.
 */
export function validateEmailFormat(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

export type MxStatus = 'idle' | 'checking' | 'valid' | 'invalid' | 'error' | 'offline';

export interface EmailValidationState {
  formatValid: boolean;
  mxStatus: MxStatus;
  mxError?: string;
}

/**
 * @hook useEmailValidation
 *
 * Purpose:
 * - Hook that provides live email format checking and on-blur MX record verification.
 *
 * Returns:
 * - formatValid, mxStatus, mxError, checkFormat(email), checkMx(email), reset()
 *
 * Side Effects:
 * - checkMx posts to /api/validate/email through the host app's reverse proxy.
 *
 * Offline Behavior:
 * - Sets mxStatus='offline' instead of issuing a network request when offline.
 */
export function useEmailValidation(_apiBase?: string) {
  // _apiBase parameter retained for API compatibility but ignored — always use relative path.
  // Relative path routes through the host app's Vite proxy (dev) or nginx (production)
  // to that app's API service. Never substitute a Docker-internal hostname here:
  // Docker service names are unreachable from the browser.
  const [state, setState] = useState<EmailValidationState>({
    formatValid: false,
    mxStatus: 'idle',
  });
  const abortRef = useRef<AbortController | null>(null);

  const checkFormat = useCallback((email: string) => {
    const formatValid = validateEmailFormat(email);
    setState((prev) => ({ ...prev, formatValid, mxStatus: formatValid ? prev.mxStatus : 'idle', mxError: undefined }));
    return formatValid;
  }, []);

  const checkMx = useCallback(async (email: string) => {
    if (!validateEmailFormat(email)) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState((prev) => ({ ...prev, mxStatus: 'checking', mxError: undefined }));

    // Can't reach the server while offline — skip the fetch entirely
    if (!navigator.onLine) {
      setState((prev) => ({ ...prev, mxStatus: 'offline', mxError: 'Unable to verify — device is offline' }));
      return;
    }

    try {
      const res = await fetch(`/api/validate/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      if (!res.ok) {
        // Endpoint may not exist in all host apps (e.g. mobile-pwa) — degrade
        // silently. The authoritative MX check on submit uses Cloudflare DNS.
        setState((prev) => ({ ...prev, mxStatus: 'idle', mxError: undefined }));
        return;
      }

      const data = await res.json();
      setState((prev) => ({
        ...prev,
        mxStatus: data.valid ? 'valid' : 'invalid',
        mxError: data.valid ? undefined : (data.reason || 'No mail server found for this domain'),
      }));
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      setState((prev) => ({
        ...prev,
        mxStatus: navigator.onLine ? 'error' : 'offline',
        mxError: navigator.onLine ? 'Could not verify email domain' : 'Unable to verify — device is offline',
      }));
    }
  }, []);

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setState({ formatValid: false, mxStatus: 'idle' });
  }, []);

  return { ...state, checkFormat, checkMx, reset };
}

/**
 * @process verifyEmailMx (one-shot, async)
 * Steps:            Format-check → POST /api/validate/email (relative, proxy-routed
 *                   to the host app's API) → map the result to a user-facing message.
 * Inputs:           email string.
 * Outputs:          null when the email is acceptable (valid format + MX present, or
 *                   the check could not run and is skipped gracefully); otherwise a
 *                   user-facing error string.
 * Failure Handling: Network/offline/endpoint-absent failures degrade to null (skip the
 *                   MX check) so a transient outage never blocks saving a contact.
 * Idempotency:      REQUIRED — pure read; no state mutated.
 *
 * Notes: Replaces direct browser calls to third-party DNS providers. The DNS-over-HTTPS
 *        lookup lives server-side in `verifyEmailMx` (@firesquirrel/core); the browser
 *        only ever talks to its own API via a relative URL (no hardcoded hostnames).
 */
export async function verifyEmailMx(email: string): Promise<string | null> {
  if (!email.trim()) return null;
  if (!validateEmailFormat(email)) return 'Invalid email format — must be like name@example.com';
  if (typeof navigator !== 'undefined' && !navigator.onLine) return null; // offline — skip MX check gracefully
  try {
    const res = await fetch('/api/validate/email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) return null; // endpoint absent / server issue — skip gracefully
    const data = await res.json();
    if (data && data.valid === false) {
      const domain = data.domain || email.split('@')[1];
      return data.reason || `No mail server (MX record) found for "${domain}". This domain cannot receive email — please verify the address.`;
    }
  } catch {
    return null; // network error — skip MX check gracefully
  }
  return null;
}
